// ===================================================================
// generateShareCard.ts
// Génère une image PNG 1080×1350 (format Instagram portrait)
// compatible Web Share API pour WhatsApp, Instagram Stories, etc.
// ===================================================================

export interface ShareCardData {
  languageName: string;
  languageFlag: string;
  wordsLearned: number;
  streakDays: number;
  totalXP: number;
  xpGained: number;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export async function generateShareCard(data: ShareCardData): Promise<Blob> {
  const W = 1080;
  const H = 1350;

  const canvas = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // ── Fond ──────────────────────────────────────────────────────────
  ctx.fillStyle = '#1E1008';
  ctx.fillRect(0, 0, W, H);

  // ── Texture kufic ─────────────────────────────────────────────────
  ctx.strokeStyle = 'rgba(200, 112, 64, 0.07)';
  ctx.lineWidth = 1.5;
  const cell = 54;
  for (let x = 0; x < W; x += cell) {
    for (let y = 0; y < H; y += cell) {
      roundRect(ctx, x + 7, y + 7, 40, 40, 4); ctx.stroke();
      roundRect(ctx, x + 17, y + 17, 20, 20, 2); ctx.stroke();
    }
  }

  // ── Bordures accent ────────────────────────────────────────────────
  ctx.fillStyle = '#C87040';
  ctx.fillRect(0, 0, W, 14);
  ctx.fillRect(0, H - 14, W, 14);
  ctx.fillRect(0, 0, 14, H);
  ctx.fillRect(W - 14, 0, 14, H);

  // ── Logo ───────────────────────────────────────────────────────────
  ctx.textAlign = 'center';
  ctx.fillStyle = '#C87040';
  ctx.font = 'bold 64px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('JOGOLINGA', W / 2, 155);

  ctx.fillStyle = 'rgba(200,112,64,0.5)';
  ctx.font = '28px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('Langues africaines • Afrikanische Sprachen', W / 2, 200);

  // ── Séparateur ─────────────────────────────────────────────────────
  ctx.strokeStyle = 'rgba(200,112,64,0.35)';
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(140, 230); ctx.lineTo(W - 140, 230); ctx.stroke();

  // ── Langue + emoji flag ────────────────────────────────────────────
  ctx.fillStyle = '#F2E6CB';
  ctx.font = 'bold 62px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText(`J'apprends le ${data.languageName}`, W / 2, 320);

  ctx.font = '84px sans-serif';
  ctx.fillText(data.languageFlag, W / 2, 420);

  // ── Carte centrale (XP gagné) ──────────────────────────────────────
  const bigCardX = 180;
  const bigCardY = 460;
  const bigCardW = W - 360;
  const bigCardH = 200;

  ctx.fillStyle = 'rgba(200, 112, 64, 0.14)';
  roundRect(ctx, bigCardX, bigCardY, bigCardW, bigCardH, 24);
  ctx.fill();

  ctx.strokeStyle = 'rgba(200, 112, 64, 0.5)';
  ctx.lineWidth = 2;
  roundRect(ctx, bigCardX, bigCardY, bigCardW, bigCardH, 24);
  ctx.stroke();

  ctx.fillStyle = '#C87040';
  ctx.font = 'bold 88px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText(`+${data.xpGained} XP`, W / 2, bigCardY + 116);

  ctx.fillStyle = '#A07848';
  ctx.font = '30px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('gagnés dans cette session', W / 2, bigCardY + 168);

  // ── 3 stats ────────────────────────────────────────────────────────
  const stats = [
    { icon: '📚', value: `${data.wordsLearned}`, label: 'mots appris' },
    { icon: '🔥', value: `${data.streakDays}`, label: data.streakDays > 1 ? 'jours de série' : 'jour de série' },
    { icon: '⭐', value: `${data.totalXP}`, label: 'XP au total' },
  ];

  const statW = 270;
  const statH = 200;
  const statGap = 30;
  const statsStartX = (W - (3 * statW + 2 * statGap)) / 2;
  const statsY = 720;

  stats.forEach((stat, i) => {
    const sx = statsStartX + i * (statW + statGap);

    ctx.fillStyle = 'rgba(42, 24, 10, 0.9)';
    roundRect(ctx, sx, statsY, statW, statH, 20);
    ctx.fill();

    ctx.strokeStyle = 'rgba(200, 112, 64, 0.3)';
    ctx.lineWidth = 1.5;
    roundRect(ctx, sx, statsY, statW, statH, 20);
    ctx.stroke();

    ctx.font = '42px sans-serif';
    ctx.fillStyle = '#F2E6CB';
    ctx.fillText(stat.icon, sx + statW / 2, statsY + 60);

    ctx.fillStyle = '#C87040';
    ctx.font = 'bold 52px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText(stat.value, sx + statW / 2, statsY + 130);

    ctx.fillStyle = '#A07848';
    ctx.font = '24px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText(stat.label, sx + statW / 2, statsY + 168);
  });

  // ── Séparateur bas ─────────────────────────────────────────────────
  ctx.strokeStyle = 'rgba(200,112,64,0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(140, 985); ctx.lineTo(W - 140, 985); ctx.stroke();

  // ── CTA ────────────────────────────────────────────────────────────
  ctx.fillStyle = '#F2E6CB';
  ctx.font = '36px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('Apprends avec moi gratuitement', W / 2, 1060);

  // Bouton CTA dessiné
  const btnW = 480;
  const btnH = 90;
  const btnX = (W - btnW) / 2;
  const btnY = 1090;

  const grad = ctx.createLinearGradient(btnX, btnY, btnX + btnW, btnY);
  grad.addColorStop(0, '#C87040');
  grad.addColorStop(1, '#7A3E18');
  ctx.fillStyle = grad;
  roundRect(ctx, btnX, btnY, btnW, btnH, 45);
  ctx.fill();

  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 40px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('jogolinga.com', W / 2, btnY + 60);

  // ── Tagline bas ────────────────────────────────────────────────────
  ctx.fillStyle = 'rgba(160, 120, 72, 0.6)';
  ctx.font = '26px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('Wolof • Bambara • Lingala • Peul • Copte • Swahili', W / 2, 1250);

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/png')
  );
}

export function buildShareText(data: ShareCardData): string {
  return (
    `🌍 Je viens d'apprendre ${data.wordsLearned} mots en ${data.languageName} ${data.languageFlag} sur Jogolinga !\n` +
    `🔥 ${data.streakDays} jour${data.streakDays > 1 ? 's' : ''} de série • ⭐ ${data.totalXP} XP\n\n` +
    `Apprends les langues africaines gratuitement :\n` +
    `👉 https://jogolinga.com`
  );
}
